import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { isArray } from 'lodash';
import React, { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { UserService } from '../../../Component/Api/User/UserService';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { InformationDialog } from '../../../Component/Common/Components/InformationDialog';
import { WarningDialog } from '../../../Component/Common/Components/WarningDialog';
import {BEE123UserTypes, usersList} from '../../../Component/Common/Constants/Lists/Users';
import { UserDto } from '../../../Component/Model/Dto/User';
import { defaultObject } from '../../../Helpers/typescriptHelper';
import { UserAtom } from '../../../State/user.Atom';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';
import { UserManagementValidationSchema } from './UserManagementValidationSchema';

interface IProps {
    userId?: string;
    showUserManagementModal: boolean;
    showOnlyClientUserTypes: boolean;
    closeUserManagementModal: () => void;
    onCreateFinished: () => void;
}

export const UserManagementModal = (props: IProps) => {

    const [userData, setUserData] = React.useState<UserDto.UserEditDto>(defaultObject);
    const currentUser = useRecoilValue(UserAtom);

    const [isLoading, setIsLoading] = React.useState(false);
    const [showSuccessfulCreateDialog, setShowSuccessfulCreateDialog] = React.useState(false);
    const [showResetPasswordDialog, setShowResetPasswordDialog] = React.useState(false);
    const [resetPasswordText, setResetPasswordText] = React.useState('');
    const [disableUserType] = React.useState(currentUser.isClientAdmin);
    const [showUserTypeWarning, setShowUserTypeWarning] = React.useState(false);
    const [pendingRequest, setPendingRequest] = React.useState<UserDto.UserEditDto | null>(null);
    const [originalUserType, setOriginalUserType] = React.useState<string | undefined>();
    const [isSaving, setIsSaving] = React.useState(false);
    const isCreateUser = props.userId ? false : true;

    const { control, handleSubmit, formState: { isSubmitting }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(UserManagementValidationSchema)
    });

    const fetchUserData = async (id: string) => {
        try {
            let response = await UserService.getById(id);
            setUserData(response.data);
            setOriginalUserType(response.data.UserType.toString());
            reset(response.data)
        } catch (error) {
            console.log(error);
        }
    }

    const executeUpdate = async (request: UserDto.UserEditDto) => {
        setIsSaving(true);
        try {
            if (isCreateUser) {
                await UserService.insertUser(request);
                setShowSuccessfulCreateDialog(true);
            } else {
                await UserService.updateUser(request);
                toast.success("User details updated");
            }
            setOriginalUserType(request.UserType.toString());
        } catch (error: any) {
            if (isArray(error.response.data)) {
                let Errors: string[] = error.response.data;
                Errors.forEach((error: string) => {
                    toast.error(error);
                });
            }
        } finally {
            setIsSaving(false);
        }
    }

    const validateRequest = async (data: FieldValues) => {
        const request: UserDto.UserEditDto = {
            Id: userData.Id,
            RegistrationId: userData.RegistrationId,
            IsTwoFactorAuthenticationConfigured: userData.IsTwoFactorAuthenticationConfigured,
            Email: data.Email,
            UserType: data.UserType,
            Name: data.Name,
            TelephoneNumber: data.TelephoneNumber,
            Username: data.Username
        }

        if (!isCreateUser && data.UserType !== originalUserType) {
            setPendingRequest(request);
            setShowUserTypeWarning(true);
            return;
        }

        await executeUpdate(request);
    }

    const getUsersList = () => {
        let filteredUsersList = usersList;

        const isSubAdminVisible = currentUser.isAdmin;
        const isAdminVisible = currentUser.isAdmin;
        const isClientAdminVisible = currentUser.isClientAdmin;
        const isUserOptionVisible = currentUser.isSubAdmin || currentUser.isAdmin;
        const isClientOptionVisible = currentUser.hasAuditRole;
        const isInternalAuditorVisible = currentUser.hasAuditRole && (currentUser.isSubAdmin || currentUser.isAdmin || currentUser.isInternalAuditor);


        if(isClientAdminVisible)
        {
            filteredUsersList =  filteredUsersList = filteredUsersList.filter(item => item.value === 'Client');
            return filteredUsersList;
        }

        if (isSubAdminVisible) {
            if (isClientOptionVisible) {
                filteredUsersList = filteredUsersList.map(item => {
                    if (item.value === 'Sub-Admin') {
                        return { ...item, value: 'Technical Signatory' };
                    }
                    else {
                        return item;
                    }
                })
            }
        }
        else {
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'Sub-Admin');
        }

        if (isUserOptionVisible) {
            if (isClientOptionVisible) {
                filteredUsersList = filteredUsersList.map(item => {
                    if (item.value === 'User') {
                        return { ...item, value: 'Analyst' };
                    }
                    else {
                        return item;
                    }
                })
            }
        } else {
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'User');
        }

        if (isClientOptionVisible) {
            filteredUsersList = filteredUsersList.map(item => {
                if (item.value === 'Sub-Admin') {
                    return { ...item, value: 'Technical Signatory' };
                }
                else if (item.value === 'User') {
                    return { ...item, value: 'Analyst' };
                }
                else {
                    return item;
                }
            })
        } else {
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'Client');
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'Scorecard Admin');
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'Client Admin');
        }

        if (!isInternalAuditorVisible) {
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'Internal Auditor');
        }

        if (!isAdminVisible) {
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'Admin');
        }

        if(props.showOnlyClientUserTypes){
            filteredUsersList = filteredUsersList.filter(item => item.value === 'Client' || item.value === 'Client Admin');
        }
        else{
            filteredUsersList = filteredUsersList.filter(item => item.value !== 'Client' && item.value !== 'Client Admin');
        }

        return filteredUsersList;
    }

    const sendResetPasswordEmail = async () => {
        setIsLoading(true);
        try {
            await UserService.userForgotPassword(userData.Username);
            setResetPasswordText('A reset password link has been emailed to the user\'s email address');
        } catch (error: any) {
            if (error.response.status === 400)
                setResetPasswordText(error.response.data.Message);
        } finally {
            setShowResetPasswordDialog(true);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (props.userId !== undefined) {
            fetchUserData(props.userId);
        } else {
            const updatedUserData = {
                ...defaultObject,
                UserType: currentUser.isClientAdmin ? BEE123UserTypes.Client : undefined
            };
            setUserData(updatedUserData);
            reset(updatedUserData);
        }


    }, [props.userId]);

    return (
        <>
            <Dialog disableEnforceFocus key={props.userId} aria-labelledby="form-dialog-title" maxWidth='sm' open={props.showUserManagementModal} onClose={props.closeUserManagementModal}>
                <CustomDialogTitle id="customized-dialog-title" onClose={props.closeUserManagementModal}>
                    {props.userId ? 'Update User' : 'Create New User'}
                </CustomDialogTitle>
                <DialogContent>
                    <form id='userDataForm' onSubmit={handleSubmit(validateRequest)}>
                        <Grid container spacing={1} mt={0.5} mb={1}>
                            <Grid item xs={12}>
                                <AppTextInput control={control} name="Name" label="Full Name" />
                            </Grid>
                            <Grid item xs={12}>
                                <AppTextInput control={control} name="Username" label="Username" />
                            </Grid>
                            <Grid item xs={12}>
                                <AppTextInput control={control} name="Email" label="Email" />
                            </Grid>
                            <Grid item xs={12}>
                                <AppTextInput control={control} name="TelephoneNumber" label="Telephone Number" />
                            </Grid>
                            <Grid item xs={12}>
                                <AppSelectList control={control} name="UserType" label='User Type' items={getUsersList()} disabled={disableUserType}/>
                            </Grid>
                        </Grid>
                    </form>
                    {props.userId ? <LoadingButton loading={isLoading} variant='contained' color='primary' onClick={sendResetPasswordEmail}>Send Reset Password Email</LoadingButton> : <></>}
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={isSubmitting || isSaving} variant='contained' type="submit" form='userDataForm' color='primary'>{isCreateUser ? "Submit" : "Save"}</LoadingButton>
                </DialogActions>
            </Dialog>
            {/* Succesful create dialog */}
            <InformationDialog
                showDialog={showSuccessfulCreateDialog}
                handleAccept={() => {
                    setShowSuccessfulCreateDialog(false);
                    reset(defaultObject);
                    props.onCreateFinished();
                }}
                heading={"User Created"}
                message={"An activation link has been emailed to the user's email address. The new user will only be able to login once the account has been activated by following the activation link."}
            />
            {/* reset password dialog */}
            <InformationDialog
                showDialog={showResetPasswordDialog}
                handleAccept={() => {
                    setShowResetPasswordDialog(false);
                }}
                heading={"BEE123"}
                message={resetPasswordText}
            />
            <WarningDialog
                showDialog={showUserTypeWarning}
                heading="Update User Type"
                message="Updating this user's type will update their roles and reset all their scorecard access. Are you sure you want to proceed?"
                handleAccept={() => {
                    setShowUserTypeWarning(false);
                    if (pendingRequest) executeUpdate(pendingRequest);
                    setPendingRequest(null);
                }}
                handleCancel={() => {
                    setShowUserTypeWarning(false);
                    setPendingRequest(null);
                }}
            />
        </>
    )
}
